import axios from "axios";
import moment from "moment";
import { API_URL } from "src/scenes/App";
import { UserKeys } from "src/constants/userDetails";

const emptyOverviewData = {
    quote: null,
    mood: null,
    moodHistory: [],
    notice: null,
    notices: [],
    survey: null,
    surveys: [],
    poll: null,
    polls: [],
    perks: [],
};

const authConfig = (token, signal) => ({
    headers: {
        Authorization: "Bearer " + token
    },
    signal
});

const sortByNewestDate = (items, keys) => [...(items || [])].sort((a, b) => {
    const getDate = item => {
        const dateValue = keys.map(key => item?.[key]).find(Boolean);
        return dateValue ? new Date(dateValue).getTime() : 0;
    };

    return getDate(b) - getDate(a);
});

const sortBySoonestEndDate = (items, keys) => [...(items || [])].sort((a, b) => {
    const getDate = item => {
        const dateValue = keys.map(key => item?.[key]).find(Boolean);
        const time = dateValue ? new Date(dateValue).getTime() : Number.MAX_SAFE_INTEGER;
        return isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
    };

    return getDate(a) - getDate(b);
});

const getMoodOverview = (responseData) => {
    const sevenDaysAgo = moment().subtract(6, "days").startOf("day");
    const todayMoodHistories = responseData.moodHistories.filter(moodHistory => {
        const moodDate = moment(moodHistory.date);
        return moodDate.isValid() && moodDate.isSame(moment(), "day");
    });
    const lastSevenDays = sortByNewestDate(responseData.moodHistories.filter(moodHistory => {
        const moodDate = moment(moodHistory.date);
        return moodDate.isValid() && moodDate.isSameOrAfter(sevenDaysAgo);
    }), ["date"]);

    return {
        today: sortByNewestDate(todayMoodHistories, ["date"])[0] || null,
        lastSevenDays,
    };
};

const getQuoteOverview = async ({ config, user }) => {
    const response = await axios.get(`${API_URL}/quotes/todaysQuote`, config);
    const quote = response.data.quote;

    if (!quote?.companyUuid) {
        return quote;
    }

    return {
        ...quote,
        companyName: user?.[UserKeys.COMPANY_NAME] || "",
    };
};

const getPollOverview = polls => {
    const activePolls = polls.filter(poll => !poll.hasEnded);

    if (activePolls.length > 0) {
        return sortByNewestDate(activePolls, ["startsAt"])[0];
    }

    return sortBySoonestEndDate(polls, ["endsAt"])[0] || null;
};

export const getEmptyDashboardOverviewData = () => ({ ...emptyOverviewData });

export const getDashboardOverviewData = async ({ user, availability, signal }) => {
    const token = user?.[UserKeys.TOKEN];
    const config = authConfig(token, signal);
    const nextData = getEmptyDashboardOverviewData();

    if (availability.quote) {
        nextData.quote = await getQuoteOverview({ config, user });
    }

    if (availability.mood) {
        const moodResponse = await axios.get(`${API_URL}/moodTracker`, config);
        const moodOverview = getMoodOverview(moodResponse.data);
        nextData.mood = moodOverview.today;
        nextData.moodHistory = moodOverview.lastSevenDays;
    }

    if (availability.notice) {
        const audience = user?.[UserKeys.IS_CLIENT] ? "clients" : "employees";
        const noticeResponse = await axios.get(`${API_URL}/noticeboard/notices/${audience}`, {
            ...config,
            params: { filter: "active" },
        });
        const notices = noticeResponse.data.notices.filter(notice => notice.deleted_at === undefined || !notice.deleted_at);
        nextData.notices = sortByNewestDate(notices, ["postedDate", "scheduleDate", "date", "createdAt"]);
        nextData.notice = nextData.notices[0] || null;
    }

    if (availability.survey) {
        const surveysResponse = await axios.get(`${API_URL}/survey/active`, config);
        nextData.surveys = sortBySoonestEndDate(surveysResponse.data.surveys, ["finishDate", "endDate", "endsAt"]);
        nextData.survey = nextData.surveys[0] || null;
    }

    if (availability.poll) {
        const pollsResponse = await axios.get(`${API_URL}/polls`, config);
        nextData.polls = pollsResponse.data.polls;
        nextData.poll = getPollOverview(nextData.polls);
    }

    if (availability.perks) {
        const perksResponse = await axios.get(`${API_URL}/perks/available`, config);
        nextData.perks = sortBySoonestEndDate(perksResponse.data.perks, ["endDate"]).slice(0, 12);
    }

    return nextData;
};
